import React, { useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Reusable pagination footer:
 *  - "Show N entries" selector (default 10, 25, 50, 100)
 *  - Numbered page buttons (Previous, 1, 2, 3, 4, 5, Next)
 *  - Emits { pageSize, page } to the parent via callbacks
 *
 * Props:
 *   page       — 1-based current page
 *   pageSize   — items per page
 *   total      — total number of items
 *   onPage     — (nextPage) => void
 *   onPageSize — (nextSize) => void
 *   pageSizeOptions — array (default [10, 25, 50, 100])
 *   testId     — string used to derive data-testids
 */
export default function Pagination({
  page,
  pageSize,
  total,
  onPage,
  onPageSize,
  pageSizeOptions = [10, 25, 50, 100],
  testId = "pagination",
}) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));
  const currentPage = Math.min(Math.max(1, page || 1), totalPages);

  // Build the visible number band (up to 5 numeric buttons, always
  // centred around the current page).
  const numbers = useMemo(() => {
    const arr = [];
    const window = 5;
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + window - 1);
    start = Math.max(1, end - window + 1);
    for (let i = start; i <= end; i += 1) arr.push(i);
    return arr;
  }, [currentPage, totalPages]);

  const from = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const to = Math.min(total, currentPage * pageSize);

  return (
    <div
      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-3 border-t border-slate-200 text-xs text-slate-500"
      data-testid={testId}
    >
      <div className="flex items-center gap-2">
        <span>Show</span>
        <select
          value={pageSize}
          onChange={(e) => { onPageSize?.(parseInt(e.target.value, 10)); onPage?.(1); }}
          data-testid={`${testId}-page-size`}
          className="h-8 px-2 rounded-md border border-slate-300 bg-white text-sm font-semibold"
        >
          {pageSizeOptions.map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
        <span>entries</span>
        <span className="ml-3 text-slate-400" data-testid={`${testId}-range`}>
          Showing {from}–{to} of {total}
        </span>
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPage?.(Math.max(1, currentPage - 1))}
          disabled={currentPage <= 1}
          data-testid={`${testId}-prev`}
          className="inline-flex items-center h-8 px-2.5 rounded-md border border-slate-300 text-slate-700 font-semibold hover:bg-slate-50 disabled:opacity-40"
        >
          <ChevronLeft className="w-3.5 h-3.5" /> Previous
        </button>
        {numbers.map((n) => (
          <button
            key={n}
            onClick={() => onPage?.(n)}
            data-testid={`${testId}-page-${n}`}
            className={`h-8 min-w-[32px] px-2 rounded-md border font-semibold ${
              n === currentPage
                ? "bg-[#0F62FE] text-white border-[#0F62FE]"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
            }`}
          >
            {n}
          </button>
        ))}
        <button
          onClick={() => onPage?.(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage >= totalPages}
          data-testid={`${testId}-next`}
          className="inline-flex items-center h-8 px-2.5 rounded-md border border-slate-300 text-slate-700 font-semibold hover:bg-slate-50 disabled:opacity-40"
        >
          Next <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
